import {gql, useLazyQuery } from "@apollo/client";
import {useLogger} from "./useLogger";
import {getBrowser} from "../common/common";
import {useCallback, useMemo, useState} from "react";
import {bookingAPI} from "../common/api/bookingAPI";
import {BOOKING_SERVICES} from "../common/routes";
import {useKeycloakFunctions} from "./useKeycloakFunctions";
import {API_RESERVATIONS} from "../common/api";
import {checkEnableServiceBookingStatusAndCancelService} from "../common/configcat/functions";
import * as Sentry from "@sentry/react";

const resortServicesByResortId = gql`query ResortAdditionalServices($hapimagResortId: Int!, $locale: String!) {
    resortAdditionalServiceCollection(where: {resort: {hapimagResortId: $hapimagResortId}, available: true}, locale: $locale, order: displayOrder_ASC) {
        items {
            image {
                url
            }
            title
            title_en_gb: title(locale: "en-GB")
            shortDescription
            price
            priceUnit
            packageType
            serviceType
            serviceNumberOptionsCollection {
                items {
                    serviceNumber
                }
            }
            displayOrder
            canBeBookedDirectly
            availableFrom
            availableTo
            sys {
                id
            }
        }
    }
}`;

const resortServiceBySysId = gql`query ResortAdditionalServiceBySysId($serviceSysId: String!, $locale: String!) {
    resortAdditionalService(id: $serviceSysId, locale: $locale) {
        image {
            url
        }
        title
        title_en_gb: title(locale: "en-GB")
        shortDescription
        fullDescription {
            json
        }
        price
        priceUnit
        priceType
        place {
            ... on ResortPlacesEntry {
                givenName,
                location {
                    lat
                    lon
                }
                distanceInMinutes
                distanceToTheResort
            }
        }
        when
        bookingInformation
        serviceNumberOptionsCollection (limit: 6) {
            items {
                serviceNumber
                title
                type
                minimum
                maximum
                optionsCollection {
                    items {
                        serviceNumber
                        title
                        type
                        minimum
                        maximum
                    }
                }
            }
        }
        externalServiceInfo {
            json
        }
        available
        availableFrom
        availableTo
        packageType
        serviceType
        placeholderForComments
        canBeBookedDirectly
        resort {
            hapimagResortId
			resortName
            resortContactDetails {
				resortPhoneNumber
				resortEmailAddress
			}
		}
        specialInstructions
        sys {
            id
        }
    }
}
`;

export const useResortServices = () => {
    const {logger} = useLogger();
    const {initialized, authenticated} = useKeycloakFunctions();

    // Internal state
    const [resortId, setResortId] = useState();
    const [reservationObjectId, setReservationObjectId] = useState();
    const [reservationSignature, setReservationSignature] = useState();
    const [currentLocale, setCurrentLocale] = useState();

    // External state
    const [resortServices, setResortServices] = useState();
    const [resortServicesLoading, setResortServicesLoading] = useState(false);
    const [resortServicesError, setResortServicesError] = useState();

    const [resortService, setResortService] = useState();
    const [resortServiceLoading, setResortServiceLoading] = useState(false);
    const [resortServiceError, setResortServiceError] = useState();

    const [requestServiceLoading, setRequestServiceLoading] = useState(false);
    const [requestServiceError, setRequestServiceError] = useState();

    const [cancelServiceLoading, setCancelServiceLoading] = useState(false);
    const [cancelServiceError, setCancelServiceError] = useState();

    // Resort content
    const [getResortServicesContnent] = useLazyQuery(resortServicesByResortId, {
        fetchPolicy: getBrowser() === "Safari" ? "network-only" : "cache-first",
        errorPolicy: "all"
    });

    const [getResortServiceContent] = useLazyQuery(resortServiceBySysId, {
        fetchPolicy: getBrowser() === "Safari" ? "network-only" : "cache-first",
        errorPolicy: "all"
    });

    // Get services with bookability context
    const getResortServices = useCallback(async (opts) => {
        const { hapimagResortId, reservationObjId, locale, reservationSignature } = opts; 
        try {

            if (!resortId ||
                resortId !== hapimagResortId ||
                reservationObjId !== reservationObjectId ||
                !locale ||
                locale !== currentLocale
            ) {
                // Re-init the state
                setResortId(hapimagResortId);
                setReservationObjectId(reservationObjId);
                setReservationSignature(reservationSignature);
                setCurrentLocale(locale);
            }

            // Check FF
            const isFFEnabled = await checkEnableServiceBookingStatusAndCancelService();

            const servicesContent = await getResortServicesContnent({variables: {hapimagResortId, locale}});

            const sihotServices = isFFEnabled && reservationObjId ? (await bookingAPI.get(
                `${BOOKING_SERVICES}?reservation_object_id=${reservationObjId}&exclude_standard_services=true`,
                { headers: {
                    "x-reservation-signature": reservationSignature
                }}
            ))?.data?.services : undefined;

            const data = servicesContent?.data?.resortAdditionalServiceCollection?.items?.map((item) => {
                const itemCodes = item?.serviceNumberOptionsCollection?.items;

                // A service might consist multiple sihot service codes
                const sihotServicesIncluded = sihotServices?.filter(
                    (s) => itemCodes.some((code) => s?.service_code === code?.serviceNumber)
                ) ?? [];

                return {
                    ...item,
                    ...(sihotServicesIncluded?.length ? {
                        booked: sihotServicesIncluded?.every(s => s.booked),
                        requested: sihotServicesIncluded?.every(s => s.requested),
                        bookedServices: sihotServicesIncluded
                    } : {
                        booked: false,
                        requested: false
                    })
                };
            });

            setResortServices(data);
        } catch (error) {
            console.error(error);
            setResortServicesError(error);
            logger(error?.message, "error", "useResortServices", error?.stack);
        } finally {
            setResortServicesLoading(false);
        }
    }, [getResortServicesContnent]);

    const getResortService = useCallback(async (serviceId, locale) => {
        setResortServiceLoading(true);
        setResortServiceError();

        try {
            const { data } = await getResortServiceContent({ variables: { serviceSysId: serviceId, locale }});
            const s = resortServices ? resortServices?.find(
                s => s?.sys?.id === data?.resortAdditionalService.sys?.id
            ) : {};

            // Aggregate the service data so the returned content service has also the booking context
            setResortService({
                ...s,
                ...data?.resortAdditionalService
            });
        } catch (err) {
            setResortServiceError(err);
        } finally {
            setResortServiceLoading(false);
        }

    }, [getResortServiceContent, JSON.stringify(resortServices)]);

    const requestService = useCallback((reservationId, resortService, extraInput, reference_url) => {
        if (initialized && authenticated) {
            setRequestServiceLoading(true);
            setRequestServiceError(null);

            const body = {
                resort_id: resortService?.resort?.hapimagResortId,
                resort_email: resortService?.resort?.resortContactDetails?.resortEmailAddress,
                service: {
                    title: resortService?.title,
                    service_ids: resortService?.serviceNumberOptionsCollection?.items?.map(option => option?.serviceNumber),
                    service_type: resortService?.packageType,
                    description: resortService?.shortDescription,
                    image_url: resortService?.image?.url + "?fm=jpg&h=332" || window.location.host + `/images/service_packagetype_${resortService?.packageType}.jpg`,
                    can_be_booked: resortService?.canBeBookedDirectly ? true : false,
                    resort_instructions: resortService?.specialInstructions,
                    extra: extraInput,
                    info_url: reference_url
                }
            };

            return bookingAPI.post(`${API_RESERVATIONS}/${reservationId}/service`, body)
                .then(async res => {
                    const isFFEnabled = await checkEnableServiceBookingStatusAndCancelService();

                    if (resortServices) {
                        const tmpServices = resortServices?.map((s) => {
                            if (s?.sys?.id === resortService?.sys?.id && s?.canBeBookedDirectly && isFFEnabled) {
                                return {
                                    ...resortService,
                                    booked: true
                                };
                            }

                            return s;
                        });

                        setResortServices(tmpServices);

                        const opts = { 
                            hapimagResortId: resortId, 
                            reservationObjId: reservationObjectId, 
                            locale: currentLocale, 
                            reservationSignature 
                        };
                        await getResortServices(opts);
                    }

                    return res;
                })
                .catch(err => {
                    console.error(err);
                    Sentry.captureException(err);
                    setRequestServiceError(err?.response);
                    throw err.response;
                })
                .finally(() => {
                    setRequestServiceLoading(false);
                });
        }
    }, [
        initialized,
        authenticated,
        resortServices,
        resortId,
        reservationObjectId,
        reservationSignature,
        currentLocale
    ]);

    const cancelService = useCallback(async (reservationId, resortService) => {
        setCancelServiceLoading(true);
        setCancelServiceError();

        try {
            const resortId = resortService?.resort?.hapimagResortId;

            await bookingAPI.delete(`${BOOKING_SERVICES}`, {
                data: {
                    reservation_id: reservationId,
                    resort_id: resortId,
                    booked: resortService?.booked,
                    requested: resortService?.requested,
                    date_from: resortService?.bookedServices?.[0]?.date_from,
                    date_to: resortService?.bookedServices?.[0]?.date_to,
                    service_title: resortService?.title,
                    service_object_ids: resortService?.bookedServices?.map(s => s.service_object_id),
                    total_price: resortService?.bookedServices?.reduce(
                        (total, current) => total + current?.total_price, 0
                    )
                }
            });
            const opts = { 
                hapimagResortId: resortId, 
                reservationObjId: reservationObjectId, 
                locale: currentLocale, 
                reservationSignature 
            };
            await getResortServices(opts);
        } catch (err) {
            setCancelServiceError(err);
            console.error(err);
        } finally {
            setCancelServiceLoading(false);
        }
    }, [
        initialized,
        authenticated,
        resortServices,
        reservationObjectId,
        currentLocale
    ]);

    const memoedValue = useMemo(() => ({
        getResortServices,
        resortServices,
        resortServicesLoading,
        resortServicesError,

        getResortService,
        resortService,
        resortServiceLoading,
        resortServiceError,

        requestService,
        requestServiceLoading,
        requestServiceError,

        cancelService,
        cancelServiceError,
        cancelServiceLoading
    }),
    [
        getResortServices,
        resortServices,
        resortServicesLoading,
        resortServicesError,

        getResortService,
        resortService,
        resortServiceLoading,
        resortServiceError,

        requestService,
        requestServiceLoading,
        requestServiceError,

        cancelService,
        cancelServiceError,
        cancelServiceLoading
    ]);

    return memoedValue;
};
